import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children"];
function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
import styled from 'foundations-theming/utils';
import { DATA_TRANSITIONAL_COMPONENT_ATTRIBUTE } from '../../constants';
import { ListContextProvider, useListContext } from './ListContext';
import { getListItemStyles, getListStyles } from './List.helpers';
import { Flex } from '../../../components/Flex/Flex';
import { View } from '../../../components/View/View';
import { Children } from 'react';
import { CSS_SPECIFICITY } from '../../constants';
import { VIEW_RESET_STYLES } from '../../../components/View/View.styles';
import { filterProps } from '../../../utils/filterProps';
import { useMapTokenPropsToCSS } from '../../../hooks/useMapTokenPropsToCSS';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledVerticalSeparator = styled.span.withConfig({
  displayName: "List__StyledVerticalSeparator"
})(({
  theme
}) => {
  return {
    inlineSize: '1px',
    blockSize: '1.1rem',
    backgroundColor: theme.color['border-core-default'],
    '&:first-child': {
      display: 'none'
    }
  };
});
export const StyledView = styled.div.withConfig({
  displayName: "List__StyledView"
})(({
  $cssStyles,
  $listStyles
}) => {
  return Object.assign({}, VIEW_RESET_STYLES, $listStyles, {
    [CSS_SPECIFICITY.OVERRIDE]: Object.assign({}, $cssStyles)
  });
});
export const ListItem = _ref => {
  let {
      children
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const {
    use,
    inline
  } = useListContext();
  const listItemStyleProps = getListItemStyles(use, inline);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [use === 'inline-divided' ? /*#__PURE__*/_jsx(StyledVerticalSeparator, {}) : null, /*#__PURE__*/_jsx(View, Object.assign({}, listItemStyleProps, {
      as: "li"
    }, rest, {
      children: children
    }))]
  });
};
ListItem.displayName = 'ListItem';
export const List = _ref2 => {
  let {
      children,
      inline = false,
      ordered = false,
      styled: styledProp = false,
      use = 'default',
      'aria-label': ariaLabel,
      childStyles,
      firstChildStyles,
      lastChildStyles,
      wrapChildren = false,
      [DATA_TRANSITIONAL_COMPONENT_ATTRIBUTE]: dataComponentAttribute
    } = _ref2,
    rest = _objectWithoutPropertiesLoose(_ref2, ["children", "inline", "ordered", "styled", "use", "aria-label", "childStyles", "firstChildStyles", "lastChildStyles", "wrapChildren", DATA_TRANSITIONAL_COMPONENT_ATTRIBUTE].map(_toPropertyKey));
  /**
   * User defined props and styles
   */
  const {
    domProps,
    tokenProps,
    callbackProps
  } = filterProps(rest);
  const cssStyles = useMapTokenPropsToCSS(tokenProps);

  /**
   * Default styles set by List
   */
  const listStyleProps = getListStyles(styledProp);
  const listStyles = useMapTokenPropsToCSS(listStyleProps);
  const listElement = ordered ? 'ol' : 'ul';
  const filteredChildren = Children.toArray(children).filter(child => child != null);

  /**
   * If wrapChildren is true, map over the children and wrap them in ListItem
   * and apply childStyles, firstChildStyles, or lastChildStyles to the wrapper.
   */
  const computedChildren = !wrapChildren ? filteredChildren : Children.map(filteredChildren, (child, index) => {
    /**
     * Imitate the behavior of the childClassName, firstChildClassName, and lastChildClassName in UIList.
     * Do not merge styles between the childStyles, firstChildStyles, and lastChildStyles.
     */
    let computedChildrenStyles = {};
    if (index === 0 && firstChildStyles) {
      computedChildrenStyles = firstChildStyles;
    } else if (index === Children.count(children) - 1 && lastChildStyles) {
      computedChildrenStyles = lastChildStyles;
    } else if (childStyles) {
      computedChildrenStyles = childStyles;
    }
    return /*#__PURE__*/_jsx(ListItem, Object.assign({}, computedChildrenStyles, {
      children: child
    }), index);
  });
  if (inline || use === 'inline-divided') {
    return /*#__PURE__*/_jsx(ListContextProvider, {
      use: use,
      inline: inline,
      children: /*#__PURE__*/_jsx(Flex, Object.assign({
        as: listElement,
        display: inline || use === 'inline-divided' ? 'inline-flex' : 'flex',
        flexDirection: "row",
        flexWrap: "wrap",
        columnGap: "200",
        alignItems: "center",
        "aria-label": ariaLabel
      }, rest, {
        [DATA_TRANSITIONAL_COMPONENT_ATTRIBUTE]: dataComponentAttribute || 'List',
        children: computedChildren
      }))
    });
  }
  return /*#__PURE__*/_jsx(ListContextProvider, {
    use: use,
    children: /*#__PURE__*/_jsx(StyledView, Object.assign({
      [DATA_TRANSITIONAL_COMPONENT_ATTRIBUTE]: dataComponentAttribute || 'List'
    }, domProps, callbackProps, {
      as: listElement,
      "aria-label": ariaLabel,
      $cssStyles: cssStyles,
      $listStyles: listStyles,
      children: computedChildren
    }))
  });
};
List.displayName = 'List';